"use client";
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import React, { useEffect } from "react";
import { useState } from "react";
import {
  Bars3Icon,
  CircleStackIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  SquaresPlusIcon,
  UserGroupIcon,
  XMarkIcon,
  CodeBracketSquareIcon,
  DocumentTextIcon,
  PhoneIcon,
  BookOpenIcon,
  DevicePhoneMobileIcon,
  InformationCircleIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
  ChatBubbleBottomCenterIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import Image from "next/image";
import LightModeMoon from "../LightModeMoon";

const products = [
  {
    name: "YouTube & Telegram Samples",
    description: "Post-level Data for Telegram and YouTube",
    href: "/post-level-data",
    icon: CircleStackIcon,
  },
  {
    name: "Comscore",
    description: "Unlock Digital Behavior using Comscore Data",
    href: "/comscore",
    icon: CodeBracketSquareIcon,
  },
  {
    name: "Princeton SMART",
    description: "Screenshot-based Mobile Activity Research Tool",
    href: "/princeton-smart",
    icon: DevicePhoneMobileIcon,
  },
];

const about = [
  {
    name: "About Us",
    description: "Learn more about the Accelerator",
    href: "/about",
    icon: InformationCircleIcon,
  },
  {
    name: "Mission & Values",
    description: "Get to know Accelerator",
    href: "/about#mission-values",
    icon: RocketLaunchIcon,
  },
  {
    name: "Our Approach",
    description: "Learn about our approach to research",
    href: "/about#our-approach",
    icon: SquaresPlusIcon,
  },
  {
    name: "Meet the Team",
    description: "Get to know the Accelerator team",
    href: "/about#team",
    icon: UserGroupIcon,
  },
];

const publications = [
  {
    name: "Publications",
    description: "Publication of the Accelerator",
    href: "/publications",
    icon: BookOpenIcon,
  },
  {
    name: "Reports",
    description: "Accelerator Exploratory Studies",
    href: "/reports",
    icon: DocumentTextIcon,
  },
  {
    name: "Blog",
    description: "Accelerator Blog",
    href: "/blog",
    icon: ComputerDesktopIcon,
  },
];

const solutions = [
  {
    name: "Use Cases",
    description: "Accelerator Use Cases",
    href: "/use-cases",
    icon: ClipboardDocumentListIcon,
  },
  {
    name: "Research Journey",
    description: "The Research Journey Transformed",
    href: "/#research-journey",
    icon: SparklesIcon,
  },
  {
    name: "Testimonials",
    description: "What our users are saying",
    href: "/#testimonials",
    icon: ChatBubbleBottomCenterIcon,
  },
];

const resources = [
  {
    name: "Documentation",
    description: "Learn how to use our tools",
    href: "/docs/changelog",
    icon: DocumentTextIcon,
  },
  {
    name: "Contact Us",
    description: "Contact the Accelerator team",
    href: "/contact",
    icon: PhoneIcon,
  },
  {
    name: "Help Center",
    description: "Get help with Accelerator",
    href: "/help-center",
    icon: InformationCircleIcon,
  },
  {
    name: "FAQs",
    description: "Frequently asked questions",
    href: "/help-center#faqs",
    icon: QuestionMarkCircleIcon,
  },
];

const OtherHeader: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      data-testid="homepage-header"
      className="absolute inset-x-0 top-0 z-50 bg-bgHome"
    >
      <nav
        aria-label="Global"
        className="flex items-center justify-between p-6 lg:px-8"
      >
        <div className="flex lg:flex-1">
          <a href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Accelerator</span>
            <>
              <Image
                alt="Accelerator Logo"
                src="/images/logo/Logowhite.png"
                height={500}
                width={500}
                className="h-5 w-auto"
              />
            </>
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-400"
            aria-label="Open main menu"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          {/* Products dropdown */}
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-white focus:border-none focus:outline-none">
              Product
              {/* <ChevronDownIcon
                aria-hidden="true"
                className="h-5 w-5 flex-none"
              /> */}
            </PopoverButton>

            <PopoverPanel className="absolute inset-x-0 top-0 -z-10  bg-slate-950 pt-14 shadow-lg ring-1 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="mx-auto flex max-w-7xl justify-center gap-x-8 px-6 py-10">
                {products.map((item) => (
                  <div
                    key={item.name}
                    className="group relative rounded-lg p-6 text-sm leading-6"
                  >
                    <a
                      href={item.href}
                      className="flex items-center gap-x-3 font-semibold text-white hover:underline"
                    >
                      <item.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-white"
                      />
                      {item.name}
                      <span className="absolute inset-0" />
                    </a>
                    <p className="mt-1 text-white">{item.description}</p>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {/* Products dropdown */}

          {/* About US dropdown */}
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-white focus:border-none focus:outline-none">
              About
              {/* <ChevronDownIcon
                aria-hidden="true"
                className="h-5 w-5 flex-none"
              /> */}
            </PopoverButton>

            <PopoverPanel className="absolute inset-x-0 top-0 -z-10 bg-slate-950 pt-14 shadow-lg ring-1 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="mx-auto flex max-w-7xl justify-center gap-x-8 px-6 py-10">
                {about.map((item) => (
                  <div
                    key={item.name}
                    className="group relative rounded-lg p-6 text-sm leading-6"
                  >
                    <a
                      href={item.href}
                      className="flex items-center gap-x-3 font-semibold text-white hover:underline"
                    >
                      <item.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-white"
                      />
                      {item.name}
                      <span className="absolute inset-0" />
                    </a>
                    <p className="mt-1 text-white">{item.description}</p>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {/* About US dropdown */}

          {/* Publications dropdown */}
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-white focus:border-none focus:outline-none">
              Publications
            </PopoverButton>

            <PopoverPanel className="absolute inset-x-0 top-0 -z-10 bg-slate-950 pt-14 shadow-lg ring-1 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="mx-auto flex max-w-7xl justify-center gap-x-8 px-6 py-10">
                {publications.map((item) => (
                  <div
                    key={item.name}
                    className="group relative rounded-lg p-6 text-sm leading-6"
                  >
                    <a
                      href={item.href}
                      className="flex items-center gap-x-3 font-semibold text-white hover:underline"
                    >
                      <item.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-white"
                      />
                      {item.name}
                      <span className="absolute inset-0" />
                    </a>
                    <p className="mt-1 text-white">{item.description}</p>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {/* Publications dropdown */}

          {/* Solutions dropdown */}
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-white focus:border-none focus:outline-none">
              Solutions
              {/* <ChevronDownIcon
                aria-hidden="true"
                className="h-5 w-5 flex-none"
              /> */}
            </PopoverButton>

            <PopoverPanel className="absolute inset-x-0 top-0 -z-10 bg-slate-950 pt-14 shadow-lg ring-1 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="mx-auto flex max-w-7xl justify-center gap-x-8 px-6 py-10">
                {solutions.map((item) => (
                  <div
                    key={item.name}
                    className="group relative rounded-lg p-6 text-sm leading-6"
                  >
                    <a
                      href={item.href}
                      className="flex items-center gap-x-3 font-semibold text-white hover:underline"
                    >
                      <item.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-white"
                      />
                      {item.name}
                      <span className="absolute inset-0" />
                    </a>
                    <p className="mt-1 text-white">{item.description}</p>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {/* Solutions dropdown */}

          {/* Resources dropdown */}
          <Popover>
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-white focus:border-none focus:outline-none">
              Resources
              {/* <ChevronDownIcon
                aria-hidden="true"
                className="h-5 w-5 flex-none"
              /> */}
            </PopoverButton>

            <PopoverPanel className="absolute inset-x-0 top-0 -z-10 bg-slate-950 pt-14 shadow-lg ring-1 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="mx-auto flex max-w-7xl justify-center gap-x-8 px-6 py-10">
                {resources.map((item) => (
                  <div
                    key={item.name}
                    className="group relative rounded-lg p-6 text-sm leading-6"
                  >
                    <a
                      href={item.href}
                      className="flex items-center gap-x-3 font-semibold text-white hover:underline"
                    >
                      <item.icon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-white"
                      />
                      {item.name}
                      <span className="absolute inset-0" />
                    </a>
                    <p className="mt-1 text-white">{item.description}</p>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
          {/* Resources dropdown */}
        </PopoverGroup>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <div className="mt-1 px-6">
            <LightModeMoon />
          </div>
          <a
            href="/auth/login?returnTo=/user-workspace"
            className="text-sm font-semibold leading-6 text-white"
          >
            Log in
          </a>

          <a
            href="/auth/login?screen_hint=signup&returnTo=/auth/verify-email"
            className="mx-4 rounded-sm border border-white bg-transparent px-3 py-0.5 text-sm font-semibold leading-6 text-white"
          >
            Sign up
          </a>
        </div>
      </nav>
      {/* Mobile menu */}
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-slate-950 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Accelerator</span>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              aria-label="Close menu"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {/* Products dropdown */}
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white">
                    Product
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {products.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-400 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* Products dropdown */}

                {/* About US dropdown */}
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white">
                    About Us
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {about.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-400 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* About US dropdown */}

                {/* Publications dropdown */}
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white">
                    Publications
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {publications.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-400 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* Publications dropdown */}

                {/* Solutions dropdown */}
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white">
                    Solutions
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {solutions.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-400 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* Solutions dropdown */}

                {/* Resources dropdown */}
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white">
                    Resources
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {resources.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-400 hover:text-white"
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* Resources dropdown */}
              </div>
              <div className="py-6">
                <a
                  href="/auth/login?returnTo=/user-workspace"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white"
                >
                  Log in <span aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      {/* Mobile menu */}
    </header>
  );
};

export default OtherHeader;
